import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { toast } from 'sonner';
import { Save, Pencil, X, Briefcase, FileText, Globe, Loader2, CheckCircle, Check, TrendingUp, Building2, Target, Shield } from 'lucide-react';
import { ScreenDocButtons } from './ScreenDocButtons';
import { useAuth } from '../contexts/AuthContext';
import { useUnsavedChanges } from '../contexts/UnsavedChangesContext';
import { ContactHoverCard } from './ContactHoverCard';
import { InfoTip } from './InfoTip';
import { FIELD_INFO, fmtRevenue1, fmtInt } from '../lib/fieldInfo';

// ─── City Coordinates for Germany Map ───────────────────────
function latLonToSvg(lat, lon) {
  const x = 72.92 * lon - 261.05;
  const y = -116.64 * lat + 6477.33;
  return { x: Math.round(x), y: Math.round(y) };
}

const CITIES = {
  'berlin': { lat: 52.52, lon: 13.405 }, 'hamburg': { lat: 53.55, lon: 9.993 },
  'munich': { lat: 48.135, lon: 11.582 }, 'münchen': { lat: 48.135, lon: 11.582 },
  'cologne': { lat: 50.937, lon: 6.96 }, 'köln': { lat: 50.937, lon: 6.96 },
  'frankfurt': { lat: 50.11, lon: 8.682 }, 'stuttgart': { lat: 48.775, lon: 9.182 },
  'düsseldorf': { lat: 51.227, lon: 6.773 }, 'dortmund': { lat: 51.514, lon: 7.468 },
  'essen': { lat: 51.455, lon: 7.012 }, 'leipzig': { lat: 51.34, lon: 12.374 },
  'dresden': { lat: 51.05, lon: 13.738 }, 'hannover': { lat: 52.375, lon: 9.732 },
  'nuremberg': { lat: 49.452, lon: 11.077 }, 'nürnberg': { lat: 49.452, lon: 11.077 },
  'bremen': { lat: 53.079, lon: 8.801 }, 'duisburg': { lat: 51.435, lon: 6.763 },
  'bochum': { lat: 51.483, lon: 7.216 }, 'wuppertal': { lat: 51.256, lon: 7.15 },
  'bielefeld': { lat: 52.022, lon: 8.532 }, 'bonn': { lat: 50.737, lon: 7.099 },
  'mannheim': { lat: 49.489, lon: 8.467 }, 'karlsruhe': { lat: 49.007, lon: 8.404 },
  'augsburg': { lat: 48.371, lon: 10.898 }, 'wiesbaden': { lat: 50.083, lon: 8.24 },
  'mainz': { lat: 49.993, lon: 8.271 }, 'aachen': { lat: 50.776, lon: 6.084 },
  'kiel': { lat: 54.323, lon: 10.123 }, 'freiburg': { lat: 47.999, lon: 7.842 },
  'rostock': { lat: 54.092, lon: 12.099 }, 'kassel': { lat: 51.313, lon: 9.497 },
  'erfurt': { lat: 50.985, lon: 11.03 }, 'magdeburg': { lat: 52.131, lon: 11.637 },
  'potsdam': { lat: 52.391, lon: 13.064 }, 'wolfsburg': { lat: 52.424, lon: 10.787 },
  'regensburg': { lat: 49.017, lon: 12.097 }, 'ulm': { lat: 48.402, lon: 9.988 },
  'heidelberg': { lat: 49.399, lon: 8.672 }, 'darmstadt': { lat: 49.878, lon: 8.651 },
  'saarbrücken': { lat: 49.234, lon: 6.997 }, 'osnabrück': { lat: 52.279, lon: 8.043 },
  'münster': { lat: 51.962, lon: 7.626 }, 'braunschweig': { lat: 52.269, lon: 10.527 },
  'mallersdorf': { lat: 48.771, lon: 12.233 }, 'straubing': { lat: 48.882, lon: 12.573 },
  'landshut': { lat: 48.537, lon: 12.152 }, 'rosenheim': { lat: 47.856, lon: 12.128 },
  'konstanz': { lat: 47.660, lon: 9.175 }, 'pforzheim': { lat: 48.892, lon: 8.699 },
  'reutlingen': { lat: 48.491, lon: 9.214 }, 'heilbronn': { lat: 49.142, lon: 9.220 },
  'siegen': { lat: 50.875, lon: 8.015 }, 'paderborn': { lat: 51.719, lon: 8.754 },
  'oldenburg': { lat: 53.144, lon: 8.214 }, 'lübeck': { lat: 53.87, lon: 10.687 },
};

function findCityCoords(text) {
  if (!text) return null;
  const lower = text.toLowerCase().replace(/[()]/g, ' ').trim();
  for (const [city, coords] of Object.entries(CITIES)) {
    if (lower.includes(city)) {
      const { x, y } = latLonToSvg(coords.lat, coords.lon);
      const displayName = city.charAt(0).toUpperCase() + city.slice(1);
      return { name: displayName, x, y };
    }
  }
  return null;
}

// Germany SVG path loaded as module reference to avoid babel-metadata-plugin crash
const germanyModule = require('./germanyPath');
const GERMANY_SVG_PATH = germanyModule.GERMANY_SVG_PATH;

function GermanyMap({ headquarters, otherLocations }) {
  const [markers, setMarkers] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function resolveLocations() {
      const allLocTexts = [
        ...(headquarters ? [headquarters] : []),
        ...(otherLocations || '').split(',').map(s => s.trim()).filter(Boolean),
      ];
      if (allLocTexts.length === 0) { setMarkers([]); return; }

      // First pass: instant local lookup
      const hqLocal = findCityCoords(headquarters);
      const resolved = new Map();
      const needsGeocode = [];

      allLocTexts.forEach(text => {
        const coord = findCityCoords(text);
        if (coord && !resolved.has(coord.name.toLowerCase())) {
          resolved.set(coord.name.toLowerCase(), {
            ...coord,
            type: (hqLocal && coord.name.toLowerCase() === hqLocal.name.toLowerCase()) ? 'hq' : 'office',
          });
        } else if (!coord) {
          needsGeocode.push(text);
        }
      });

      // Show local results immediately
      if (!cancelled) setMarkers(Array.from(resolved.values()));

      // Second pass: geocode unknowns via backend
      if (needsGeocode.length > 0) {
        setLoading(true);
        for (const text of needsGeocode) {
          try {
            const res = await api.geocodeCity(text);
            const g = res.data;
            if (g.lat && g.lon && g.found !== false) {
              const { x, y } = latLonToSvg(g.lat, g.lon);
              const key = g.name.toLowerCase();
              if (!resolved.has(key)) {
                const isHQ = headquarters && text === headquarters;
                resolved.set(key, { name: g.name, x, y, type: isHQ ? 'hq' : 'office' });
              }
            }
          } catch { /* skip failed geocodes */ }
        }
        if (!cancelled) {
          setMarkers(Array.from(resolved.values()));
          setLoading(false);
        }
      }
    }

    resolveLocations();
    return () => { cancelled = true; };
  }, [headquarters, otherLocations]);

  const allMarkers = markers;
  const noLocations = allMarkers.length === 0 && !loading;

  const toPercent = (x, y) => ({
    left: `${((x - 100) / 800) * 100}%`,
    top: `${(y / 1000) * 100}%`,
  });

  return (
    <div className="flex flex-col h-full">
      <p className="text-[10px] text-foreground/40 uppercase tracking-wider mb-2">German Footprint</p>
      <div className="relative" style={{ minHeight: '240px' }}>
        <svg viewBox="100 0 800 1000" className="w-full" style={{ maxHeight: '260px' }} xmlns="http://www.w3.org/2000/svg">
          <path d={GERMANY_SVG_PATH} fill="#1e293b" fillRule="evenodd" stroke="#475569" strokeWidth="1.5" />
        </svg>
        {allMarkers.map((m, i) => {
          const pos = toPercent(m.x, m.y);
          const isHQ = m.type === 'hq';
          return (
            <div key={m.name} className="absolute" style={{ left: pos.left, top: pos.top }}>
              <div className={`rounded-full border-2 border-white shadow-lg ${isHQ ? 'w-5 h-5 bg-blue-500' : 'w-3.5 h-3.5 bg-purple-500'}`}
                style={{ transform: 'translate(-50%, -50%)' }}>
                {isHQ && <div className="w-2 h-2 bg-white rounded-full mx-auto mt-[3px]" />}
              </div>
              <div className="absolute left-3 top-1/2 -translate-y-1/2 whitespace-nowrap" style={{ textShadow: '0 1px 4px rgba(0,0,0,0.9)' }}>
                <p className={`text-white font-bold ${isHQ ? 'text-sm' : 'text-xs'}`}>{m.name}</p>
              </div>
            </div>
          );
        })}
        {loading && <p className="absolute inset-0 flex items-center justify-center text-xs text-muted-foreground">Locating...</p>}
        {noLocations && <p className="absolute inset-0 flex items-center justify-center text-xs text-muted-foreground">No German locations</p>}
      </div>
      {!noLocations && (
        <div className="flex items-center gap-4 mt-1">
          {allMarkers.some(m => m.type === 'hq') && <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-full bg-blue-500 border-2 border-white"><div className="w-1 h-1 bg-white rounded-full mx-auto mt-[3px]" /></div><span className="text-[9px] text-foreground/40">HQ</span></div>}
          {allMarkers.some(m => m.type === 'office') && <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-full bg-purple-500 border border-white" /><span className="text-[9px] text-foreground/40">Office</span></div>}
        </div>
      )}
    </div>
  );
}

// ─── Sub-components ──────────────────────────────────────────

function SourceIcon({ pageRefs, field, docLabel, dealType }) {
  const ref = (pageRefs || {})[field];
  if (!ref) return null;
  const pages = ref.pages || [];
  if (pages.length === 0) return null;
  const found = ref.found !== false;
  const label = docLabel || 'Doc';
  // Check if source is a URL (web scrape)
  const isUrl = pages.some(p => String(p).startsWith('http'));
  const sourceText = isUrl
    ? `Source: ${pages[0]}`
    : `${label}, page${pages.length > 1 ? 's' : ''} ${pages.join(', ')}`;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className={`inline-flex items-center ml-1 cursor-help ${found ? 'text-blue-400/40' : 'text-amber-400/40'} hover:opacity-100`}>
          <FileText className="w-2.5 h-2.5" />
        </span>
      </TooltipTrigger>
      <TooltipContent side="top" className="bg-popover border-border text-popover-foreground text-xs max-w-[300px]">
        <p className="font-medium">{found ? 'Found in' : 'May be in'} {sourceText}</p>
      </TooltipContent>
    </Tooltip>
  );
}

function BatchSourceIcon({ dealType }) {
  if (dealType !== 'proprietary') return null;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex items-center ml-1 cursor-help text-muted-foreground/30 hover:opacity-100">
          <FileText className="w-2.5 h-2.5" />
        </span>
      </TooltipTrigger>
      <TooltipContent side="top" className="bg-popover border-border text-popover-foreground text-xs">
        <p className="font-medium">Source: Markus by Moody's</p>
      </TooltipContent>
    </Tooltip>
  );
}

function Field({ label, field, placeholder, editing, value, onChange, pageRefs, docLabel, dealType, info, format }) {
  const hasDocSource = pageRefs && (pageRefs[field]?.pages?.length > 0);
  return (
    <div>
      <p className="text-[10px] text-foreground/40 uppercase tracking-wider mb-1 flex items-center">
        {label}
        {info && <InfoTip text={info} className="ml-1" />}
        {hasDocSource
          ? <SourceIcon pageRefs={pageRefs} field={field} docLabel={docLabel} dealType={dealType} />
          : <BatchSourceIcon dealType={dealType} />
        }
      </p>
      {editing
        ? <Input value={value || ''} onChange={e => onChange(field, e.target.value)} placeholder={placeholder} className="bg-secondary border-border rounded-sm h-8 text-sm text-foreground" />
        : <p className="text-sm text-foreground">{value ? (format ? format(value) : value) : <span className="text-foreground/20">{'\u2014'}</span>}</p>
      }
    </div>
  );
}

function TextArea({ label, field, placeholder, rows, editing, value, onChange }) {
  return (
    <div>
      <p className="text-[10px] text-foreground/40 uppercase tracking-wider mb-1">{label}</p>
      {editing
        ? <textarea value={value || ''} onChange={e => onChange(field, e.target.value)} placeholder={placeholder} rows={rows || 3} className="w-full bg-secondary border border-border rounded-sm text-sm text-foreground p-2.5 outline-none resize-none leading-relaxed" />
        : <p className="text-sm text-foreground/80 leading-relaxed whitespace-pre-line">{value || <span className="text-foreground/20">{'\u2014'}</span>}</p>
      }
    </div>
  );
}

function DropdownField({ label, field, options, editing, value, onChange, info }) {
  return (
    <div>
      <p className="text-[10px] text-foreground/40 uppercase tracking-wider mb-1 flex items-center">{label}{info && <InfoTip text={info} className="ml-1" />}</p>
      {editing ? (
        <Select value={value || ''} onValueChange={v => onChange(field, v)}>
          <SelectTrigger className="bg-secondary border-border rounded-sm h-8 text-sm"><SelectValue placeholder="Select..." /></SelectTrigger>
          <SelectContent className="bg-card border-border rounded-sm">
            {options.map(o => <SelectItem key={o.value} value={o.value} className="text-sm">{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
      ) : (
        <p className="text-sm text-foreground">{options.find(o => o.value === value)?.label || value || <span className="text-foreground/20">{'\u2014'}</span>}</p>
      )}
    </div>
  );
}

function ScrapeButton({ projectId, section, label, screening, onStarted, companyWebsite }) {
  const [loading, setLoading] = useState(false);
  const handleScrape = async () => {
    if (!companyWebsite) { toast.error('No company website set'); return; }
    setLoading(true);
    try {
      await api.scrapeSection(projectId, section);
      if (onStarted) onStarted();
      toast.success(`${label} scraping started`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    setLoading(false);
  };
  const isProcessing = screening || loading;
  return (
    <Button variant="ghost" onClick={handleScrape} disabled={isProcessing || !companyWebsite}
      className="text-muted-foreground hover:text-foreground h-7 px-2 text-[10px]" data-testid={`scrape-${section}-btn`}>
      {isProcessing ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Globe className="w-3 h-3 mr-1" />}
      {isProcessing ? 'Scraping...' : `Scrape ${label}`}
    </Button>
  );
}

// ─── Section Header ──────────────────────────────────────────

function SectionHeader({ icon: Icon, title, children }) {
  return (
    <div className="flex items-center justify-between mb-3 mt-6 first:mt-0">
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4 text-foreground/50" />
        <p className="font-heading text-sm text-foreground uppercase tracking-wider">{title}</p>
      </div>
      {children && <div className="flex items-center gap-2">{children}</div>}
    </div>
  );
}

// ─── Dropdown Option Constants ───────────────────────────────

const RECURRING_REVENUE_OPTIONS = [{value:'strong',label:'Strong'},{value:'medium',label:'Medium'},{value:'weak',label:'Weak'}];
const INFRA_ADJACENT_OPTIONS = [{value:'fit',label:'Fit'},{value:'fringe',label:'Fringe'},{value:'no_fit',label:'No Fit'}];
const SUCCESSION_OPTIONS = [{value:'yes',label:'Yes'},{value:'maybe',label:'Maybe'},{value:'no',label:'No'}];
const MOAT_OPTIONS = [{value:'high',label:'High'},{value:'medium',label:'Medium'},{value:'low',label:'Low'}];
const LEVERS_OPTIONS = [{value:'abundant',label:'Abundant'},{value:'available',label:'Available'},{value:'minimal',label:'Minimal'}];
const OLTA_SECTOR_OPTIONS = [{value:'Energy',label:'Energy'},{value:'Utilities',label:'Utilities'},{value:'Transportation',label:'Transportation'},{value:'Digital',label:'Digital'},{value:'Social/Public',label:'Social/Public'}];

const VOTE_OPTIONS_UI = [
  { value: 'approve', label: 'Approve', activeClass: 'bg-emerald-900/30 text-emerald-400 border border-emerald-800/30' },
  { value: 'neutral', label: 'Neutral', activeClass: 'bg-amber-900/30 text-amber-400 border border-amber-800/30' },
  { value: 'reject', label: 'Reject', activeClass: 'bg-red-900/30 text-red-400 border border-red-800/30' },
];

function VoteBadge({ vote }) {
  if (!vote) return <span className="text-[10px] text-muted-foreground/40 font-mono">NOT VOTED</span>;
  const styles = {
    approve: 'text-emerald-400 bg-emerald-900/20 border-emerald-800/30',
    neutral: 'text-amber-400 bg-amber-900/20 border-amber-800/30',
    reject: 'text-red-400 bg-red-900/20 border-red-800/30',
  };
  return <span className={`text-[10px] font-mono px-2 py-0.5 rounded-sm border ${styles[vote] || ''}`}>{vote.toUpperCase()}</span>;
}

// ─── Main Component ──────────────────────────────────────────

export function ExecutiveSummary({ projectId, project, documents, screeningStatus, onScreenStarted, companyWebsite, onRefresh, isAcMember }) {
  const [editing, setEditing] = useState(false);
  const [data, setData] = useState(project.executive_summary || {});
  const [saving, setSaving] = useState(false);
  const [sectorConfig, setSectorConfig] = useState({});
  const [approvalOpen, setApprovalOpen] = useState(false);
  const [votes, setVotes] = useState([]);
  const [canSeeAll, setCanSeeAll] = useState(false);
  const [votingFor, setVotingFor] = useState(null);
  const [approving, setApproving] = useState(false);
  const { isAdmin, user } = useAuth();
  const [workflowRoles, setWorkflowRoles] = useState(null);
  useEffect(() => {
    api.getWorkflowAssignments().then(r => setWorkflowRoles(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    // Don't clobber in-progress edits when the project refreshes (e.g. after adding a highlight).
    if (project.executive_summary && !editing) setData(project.executive_summary);
  }, [project.executive_summary, editing]);

  useEffect(() => {
    api.getSectorConfig().then(r => setSectorConfig(r.data.sectors || {})).catch(() => {});
  }, []);

  const update = (field, value) => {
    setData(prev => ({ ...prev, [field]: value }));
  };

  const dirty = editing && JSON.stringify(data || {}) !== JSON.stringify(project.executive_summary || {});

  // Warn before leaving (reload / close tab) when there are unsaved edits.
  useEffect(() => {
    if (!dirty) return undefined;
    const handler = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  // Block in-app navigation (Back button / sidebar links) while there are unsaved edits.
  const { setUnsavedChanges } = useUnsavedChanges();
  useEffect(() => {
    setUnsavedChanges(dirty);
    return () => setUnsavedChanges(false);
  }, [dirty, setUnsavedChanges]);

  const save = async () => {
    setSaving(true);
    const toSave = { ...data };
    try {
      if (toSave.revenue && toSave.ebitda) {
        toSave.margin = String(Math.round((parseFloat(toSave.ebitda) / parseFloat(toSave.revenue)) * 1000) / 10);
      }
    } catch { /* margin calc: non-numeric values are expected */ }
    try { await api.updateCompanyInfo(projectId, { executive_summary: toSave }); toast.success('Saved'); setEditing(false); setData(toSave); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const loadVotes = async () => {
    try {
      const res = await api.getVotes(projectId);
      setVotes(res.data.votes || []);
      setCanSeeAll(res.data.can_see_all);
    } catch { /* silent */ }
  };

  const handleOpenApproval = async () => {
    await loadVotes();
    setApprovalOpen(true);
  };

  const handleVote = async (vote, voterName) => {
    setVotingFor(`${voterName}-${vote}`);
    try { await api.castVote(projectId, vote, voterName); await loadVotes(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Vote failed'); }
    finally { setVotingFor(null); }
  };

  const handleFinalize = async () => {
    setApproving(true);
    try { await api.approveDeal(projectId); toast.success('Deal approved — moved to OLTA Approved'); setApprovalOpen(false); if (onRefresh) onRefresh(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Approval failed'); }
    finally { setApproving(false); }
  };

  const approveCount = votes.filter(v => v.vote === 'approve').length;
  const canFinalize = approveCount >= 2;

  const d = data;
  const pageRefs = project.exec_page_references || {};
  const screenedDoc = project.exec_screened_doc || {};
  const docLabel = screenedDoc.label || '';
  const fp = { editing, pageRefs, docLabel, dealType: project.deal_type, onChange: update };
  const screenStatus = project.screen_status || {};
  const canApprove = isAdmin && project.pipeline_stage === 'olta_reviewed';

  // Vote widget: configured workflow approvers (drafter/approver) + admins
  const voterIds = [workflowRoles?.letter_drafter?.id, workflowRoles?.letter_approver?.id].filter(Boolean);
  const isVoter = user?.role === 'admin' || (user && voterIds.includes(user.id));
  const showVoteWidget = project.pipeline_stage === 'olta_reviewed' && !isAdmin && isVoter;

  return (
    <TooltipProvider delayDuration={200}>
    <div className="bg-card border border-border rounded-sm p-5" data-testid="executive-summary">
      {/* Top Bar */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Briefcase className="w-4 h-4 text-foreground/60" />
          <p className="font-heading text-base text-foreground">Executive Summary</p>
        </div>
        <div className="flex items-center gap-2">
          {editing ? (
            <>
              <Button onClick={save} disabled={saving} className="bg-green-700 hover:bg-green-600 text-white rounded-sm text-[11px] h-7 px-2.5"><Save className="w-3 h-3 mr-1" /> Save</Button>
              <Button variant="ghost" onClick={() => { setEditing(false); setData(project.executive_summary || {}); }} className="text-muted-foreground h-7 px-2 text-[11px]"><X className="w-3 h-3" /></Button>
            </>
          ) : !isAcMember ? (
            <>
              {project.deal_type === 'brokered' && (
                <ScreenDocButtons projectId={projectId} documents={documents} section="executive-summary"
                  screening={screeningStatus === 'processing'} onStarted={onScreenStarted} label="Executive Summary" companyWebsite={companyWebsite} />
              )}
              <Button variant="ghost" onClick={() => setEditing(true)} className="text-muted-foreground hover:text-foreground h-7 px-2 text-[11px]"><Pencil className="w-3 h-3 mr-1" /> Edit</Button>
            </>
          ) : null}
        </div>
      </div>

      {/* Unsaved-changes guard */}
      {dirty && (
        <div className="flex items-center justify-between gap-3 mb-4 px-3 py-2 rounded-sm bg-amber-900/20 border border-amber-700/40" data-testid="unsaved-changes-banner">
          <p className="text-xs text-amber-300 flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse shrink-0" />
            You have unsaved changes — don't forget to save.
          </p>
          <div className="flex items-center gap-2 shrink-0">
            <Button variant="ghost" onClick={() => { setEditing(false); setData(project.executive_summary || {}); }}
              className="text-muted-foreground h-7 px-2 text-[11px]" data-testid="discard-changes-btn">Discard</Button>
            <Button onClick={save} disabled={saving} data-testid="save-changes-banner-btn"
              className="bg-green-700 hover:bg-green-600 text-white rounded-sm text-[11px] h-7 px-2.5">
              <Save className="w-3 h-3 mr-1" /> Save now
            </Button>
          </div>
        </div>
      )}

      {/* ═══ 1. SNAPSHOT ═══ */}
      <SectionHeader icon={TrendingUp} title="Snapshot">
        {!editing && !isAcMember && project.deal_type === 'brokered' && (
          <ScreenDocButtons projectId={projectId} documents={documents} section="executive-summary"
            screening={screeningStatus === 'processing'} onStarted={onScreenStarted} label="Snapshot" companyWebsite={companyWebsite} />
        )}
      </SectionHeader>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-3">
        <Field label="Company Name" field="company_name" placeholder="e.g. Krantz GmbH" value={d.company_name} {...fp} />
        <Field label="Founded" field="founded" placeholder="e.g. 1882" value={d.founded || d.founding_date} info={FIELD_INFO.founded} {...fp} />
        <div>
          <p className="text-[10px] text-foreground/40 uppercase tracking-wider mb-1 flex items-center gap-1 flex-wrap">
            Employees
            <InfoTip text={FIELD_INFO.employees} />
            <SourceBadges source={d.employees_source} asOf={d.employees_as_of} />
          </p>
          {editing ? (
            <>
              <Input value={d.employees || ''} onChange={e => update('employees', e.target.value)} placeholder="e.g. 450"
                className="bg-secondary border-border rounded-sm h-8 text-sm text-foreground" />
              <SourceAsOfEdit source={d.employees_source} asOf={d.employees_as_of} testPrefix="emp"
                onSource={v => update('employees_source', v)} onAsOf={v => update('employees_as_of', v)} />
            </>
          ) : (
            <p className="text-sm text-foreground">{fmtInt(d.employees) || <span className="text-foreground/20">{'\u2014'}</span>}</p>
          )}
        </div>
        <div>
          <p className="text-[10px] text-foreground/40 uppercase tracking-wider mb-1 flex items-center gap-1 flex-wrap">
            Revenue (€m)
            <InfoTip text={FIELD_INFO.revenue} />
            <SourceBadges source={d.revenue_source} asOf={d.financials_as_of} />
          </p>
          {editing ? (
            <>
              <Input value={d.revenue || ''} onChange={e => update('revenue', e.target.value)} placeholder="e.g. 45.6"
                className="bg-secondary border-border rounded-sm h-8 text-sm text-foreground" />
              <SourceAsOfEdit source={d.revenue_source} asOf={d.financials_as_of} testPrefix="fin"
                onSource={v => update('revenue_source', v)} onAsOf={v => update('financials_as_of', v)} />
            </>
          ) : (
            <p className="text-sm text-foreground">{fmtRevenue1(d.revenue) || <span className="text-foreground/20">{'\u2014'}</span>}</p>
          )}
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-3">
        <div>
          <p className="text-[10px] text-foreground/40 uppercase tracking-wider mb-1 flex items-center gap-1 flex-wrap">
            EBITDA (€m)
            <SourceBadges source={d.revenue_source} asOf={d.financials_as_of} />
          </p>
          {editing ? (
            <Input value={d.ebitda || ''} onChange={e => update('ebitda', e.target.value)} placeholder="e.g. 8.2"
              className="bg-secondary border-border rounded-sm h-8 text-sm text-foreground" />
          ) : (
            <p className="text-sm text-foreground">{d.ebitda || <span className="text-foreground/20">{'\u2014'}</span>}</p>
          )}
        </div>
        <Field label="Margin (%)" field="margin" placeholder="auto-calculated" value={d.margin} {...fp} />
        <DropdownField label="OLTA Sector" field="olta_sector" options={OLTA_SECTOR_OPTIONS} info={FIELD_INFO.sector}
          editing={editing} value={d.olta_sector || d.sector} onChange={(f, v) => { update(f, v); update('subsector', ''); }} />
        {editing ? (
          <div>
            <p className="text-[10px] text-foreground/40 uppercase tracking-wider mb-1 flex items-center">Subsector<InfoTip text={FIELD_INFO.subsector} className="ml-1" /></p>
            <Select value={d.subsector || ''} onValueChange={v => update('subsector', v)}>
              <SelectTrigger className="bg-secondary border-border rounded-sm h-8 text-sm">
                <SelectValue placeholder={d.olta_sector ? 'Select subsector...' : 'Select sector first'} />
              </SelectTrigger>
              <SelectContent className="bg-card border-border rounded-sm max-h-[400px]">
                {(sectorConfig[d.olta_sector] || []).map(s => (
                  <SelectItem key={s} value={s} className="text-xs">{s}</SelectItem>
                ))}
                {!(sectorConfig[d.olta_sector] || []).length && (
                  <p className="text-xs text-muted-foreground px-3 py-2">
                    {d.olta_sector ? 'No subsectors configured' : 'Please select a sector first'}
                  </p>
                )}
              </SelectContent>
            </Select>
          </div>
        ) : (
          <div>
            <p className="text-[10px] text-foreground/40 uppercase tracking-wider mb-1 flex items-center">Subsector<InfoTip text={FIELD_INFO.subsector} className="ml-1" /></p>
            <p className="text-sm text-foreground">{d.subsector || <span className="text-foreground/20">{'\u2014'}</span>}</p>
          </div>
        )}
      </div>

      {/* ═══ 2. COMPANY OVERVIEW ═══ */}
      <SectionHeader icon={Building2} title="Company Overview">
        {!editing && !isAcMember && project.deal_type === 'brokered' && (
          <ScreenDocButtons projectId={projectId} documents={documents} section="company-overview"
            screening={screenStatus.company_overview === 'processing'} onStarted={onScreenStarted} label="Company" companyWebsite={companyWebsite} />
        )}
      </SectionHeader>
      <div className="space-y-3 mb-3">
        <TextArea label="Company Description (incl. history)" field="description" placeholder="Company history, overview, market position..." rows={3} editing={editing} value={d.description} onChange={update} />
        <TextArea label="Products / Services Description" field="products_services" placeholder="Main products, services, business lines..." rows={3} editing={editing} value={d.products_services} onChange={update} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-3">
        <div className="space-y-3">
          <Field label="Headquarters" field="headquarters" placeholder="e.g. Essen, Germany" value={d.headquarters} {...fp} />
          <Field label="Other Offices / Locations" field="other_locations" placeholder="e.g. Hamburg, Munich, Berlin" value={d.other_locations} {...fp} />
          <ShareholderPieChart projectId={projectId} project={project} onRefresh={onRefresh} isAcMember={isAcMember} />
          <ManagementTeamField label="Management Team (incl. tenors)" field="management_team" placeholder="e.g. CEO: Hans Müller (since 2015); CFO: Anna Schmidt (since 2020)" editing={editing} value={d.management_team || d.management} onChange={update} />
          <TextArea label="Management Notes" field="management_notes" placeholder="Notes on management team..." rows={2} editing={editing} value={d.management_notes} onChange={update} />
          <TextArea label="Timeline" field="timeline" placeholder="Key timeline events, deadlines..." rows={2} editing={editing} value={d.timeline || d.transaction_overview} onChange={update} />
        </div>
        <div className="bg-secondary/50 border border-border rounded-sm p-3 flex items-center justify-center">
          <GermanyMap headquarters={d.headquarters} otherLocations={d.other_locations} />
        </div>
      </div>

      {/* ═══ 3. MARKET OVERVIEW ═══ */}
      <SectionHeader icon={Target} title="Market Overview">
        {!editing && !isAcMember && project.deal_type === 'brokered' && (
          <ScreenDocButtons projectId={projectId} documents={documents} section="market-overview"
            screening={screenStatus.market_overview === 'processing'} onStarted={onScreenStarted} label="Market" companyWebsite={companyWebsite} />
        )}
      </SectionHeader>
      <div className="space-y-3 mb-3">
        <TextArea label="Macro Trend / Industry Growth" field="macro_trend" placeholder="AI-generated market trend analysis..." rows={2} editing={editing} value={d.macro_trend} onChange={update} />
        <TextArea label="Market Fragmentation" field="market_fragmentation" placeholder="AI-generated fragmentation analysis..." rows={2} editing={editing} value={d.market_fragmentation} onChange={update} />
        <TextArea label="Moat / Differentiating Features / Market Position" field="moat" placeholder="AI-generated competitive analysis..." rows={2} editing={editing} value={d.moat} onChange={update} />
      </div>

      {/* ═══ 4. OLTA VERDICT ═══ */}
      <SectionHeader icon={Shield} title="OLTA Verdict" />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-3">
        <DropdownField label="Infrastructure-adjacent?" field="infrastructure_adjacent"
          options={INFRA_ADJACENT_OPTIONS} info={FIELD_INFO.infrastructure_adjacent}
          editing={editing} value={d.infrastructure_adjacent} onChange={update} />
        <TextArea label="Rationale" field="infrastructure_adjacent_rationale" placeholder="Why is this infrastructure-adjacent?" rows={2} editing={editing} value={d.infrastructure_adjacent_rationale} onChange={update} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-3">
        <DropdownField label="Succession Case?" field="succession_case"
          options={SUCCESSION_OPTIONS} info={FIELD_INFO.succession_case}
          editing={editing} value={d.succession_case} onChange={update} />
        <TextArea label="Rationale" field="succession_case_rationale" placeholder="Is this a succession case?" rows={2} editing={editing} value={d.succession_case_rationale} onChange={update} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-3">
        <DropdownField label="Recurring Revenue Potential" field="recurring_revenue_potential"
          options={RECURRING_REVENUE_OPTIONS} info={FIELD_INFO.recurring_revenue}
          editing={editing} value={d.recurring_revenue_potential} onChange={update} />
        <TextArea label="Recurring Revenue Rationale" field="recurring_revenue_rationale" placeholder="Explain the recurring revenue assessment..." rows={2} editing={editing} value={d.recurring_revenue_rationale} onChange={update} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-3">
        <DropdownField label="Business Model Moat" field="business_model_moat"
          options={MOAT_OPTIONS}
          editing={editing} value={d.business_model_moat} onChange={update} />
        <TextArea label="Moat Rationale" field="business_model_moat_rationale" placeholder="What creates the moat?" rows={2} editing={editing} value={d.business_model_moat_rationale} onChange={update} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-3">
        <DropdownField label="Levers to reach 10x in 5yrs" field="levers_10x"
          options={LEVERS_OPTIONS}
          editing={editing} value={d.levers_10x} onChange={update} />
        <TextArea label="Levers Rationale" field="levers_10x_rationale" placeholder="What levers exist to reach 10x?" rows={2} editing={editing} value={d.levers_10x_rationale} onChange={update} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <TextArea label="Contact Strategy" field="contact_strategy" placeholder="Approach strategy..." rows={2} editing={editing} value={d.contact_strategy} onChange={update} />
        <TextArea label="Rationale" field="contact_strategy_rationale" placeholder="Contact strategy rationale..." rows={2} editing={editing} value={d.contact_strategy_rationale} onChange={update} />
      </div>
    </div>

    {/* Highlights & Risks */}
    <HighlightsRisks projectId={projectId} project={project} onRefresh={onRefresh} readOnly={isAcMember} />

    {/* Voting Dialog */}
    <Dialog open={approvalOpen} onOpenChange={setApprovalOpen}>
      <DialogContent className="bg-card border-border rounded-sm max-w-md">
        <DialogHeader>
          <DialogTitle className="text-foreground flex items-center gap-2">
            <Shield className="w-5 h-5 text-emerald-400" /> OLTA Team Approval
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <p className="text-xs text-muted-foreground">Majority (2/3) approve votes required to finalize.</p>

          {/* Team votes — Talu can vote for everyone */}
          {canSeeAll ? (
            <div className="space-y-2">
              {['Talu', 'Max', 'Rüdiger'].map(name => {
                const vote = votes.find(v => (v.voter_name || '').toLowerCase().startsWith(name.toLowerCase()) || (v.voter_key || '').toLowerCase().startsWith(name.toLowerCase()));
                return (
                  <div key={name} className="flex items-center justify-between px-3 py-2.5 bg-secondary/50 border border-border rounded-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-blue-600/20 flex items-center justify-center text-blue-400 text-[10px] font-bold font-mono">
                        {name.charAt(0)}
                      </div>
                      <span className="text-sm text-foreground font-medium">{name}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      {VOTE_OPTIONS_UI.map(opt => (
                        <Button key={opt.value} variant="ghost" size="sm"
                          onClick={() => handleVote(opt.value, name)}
                          disabled={votingFor !== null}
                          className={`h-7 px-2 text-[10px] rounded-sm ${vote?.vote === opt.value ? opt.activeClass : 'text-muted-foreground hover:text-foreground'}`}
                        >
                          {votingFor === `${name}-${opt.value}` ? <Loader2 className="w-3 h-3 animate-spin" /> : opt.label}
                        </Button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground text-center py-4">Loading votes...</p>
          )}

          <div className="flex items-center justify-between pt-2 border-t border-border">
            <span className="text-xs text-muted-foreground">
              {approveCount}/3 approve votes ({canFinalize ? 'majority reached' : 'need more votes'})
            </span>
            <Button onClick={handleFinalize} disabled={!canFinalize || approving}
              data-testid="finalize-approval-btn"
              className="bg-emerald-700 hover:bg-emerald-600 text-white rounded-sm text-xs h-8 px-4 disabled:opacity-40">
              {approving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <CheckCircle className="w-3.5 h-3.5 mr-1.5" />}
              Finalize Approval
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>

    </TooltipProvider>
  );
}


// ─── Financials source / as-of helpers (Talu, 18.08.2026) ─────

// Legacy imports carried free-text values ("Estimated (WZ peer ratio)", "Reported", "n/a") —
// only a clear A/E ever shows a badge (Max, 25.08.2026).
const normalizeSource = (s) => {
  const v = (s || '').trim().toUpperCase();
  if (v === 'A' || v.startsWith('ACTUAL') || v.startsWith('REPORT')) return 'A';
  if (v === 'E' || v.startsWith('ESTIMAT') || v.startsWith('PROJECT')) return 'E';
  return '';
};

function SourceBadges({ source, asOf }) {
  const src = normalizeSource(source);
  return (
    <>
      {src && (
        <span className={`text-[8px] font-mono px-1 rounded ${src === 'E'
          ? 'text-amber-400 bg-amber-400/10 border border-amber-400/20'
          : 'text-emerald-400 bg-emerald-400/10 border border-emerald-400/20'}`}>
          {src === 'E' ? 'Est.' : 'Actual'}
        </span>
      )}
      {asOf && <span className="text-[8px] font-mono text-muted-foreground/50">as of {asOf}</span>}
    </>
  );
}

function SourceAsOfEdit({ source, asOf, onSource, onAsOf, testPrefix }) {
  const src = normalizeSource(source);
  return (
    <div className="flex items-center gap-1 mt-1">
      {['A', 'E'].map(v => (
        <button key={v} type="button" onClick={() => onSource(src === v ? '' : v)}
          data-testid={`${testPrefix}-source-${v}`}
          className={`text-[9px] font-mono px-1.5 py-0.5 rounded border transition-colors ${src === v
            ? (v === 'E' ? 'text-amber-400 bg-amber-400/10 border-amber-400/40' : 'text-emerald-400 bg-emerald-400/10 border-emerald-400/40')
            : 'text-muted-foreground border-border hover:text-foreground'}`}>
          {v === 'E' ? 'Est.' : 'Actual'}
        </button>
      ))}
      <Input value={asOf || ''} onChange={e => onAsOf(e.target.value)} placeholder="as of (e.g. FY2025)"
        data-testid={`${testPrefix}-asof-input`}
        className="bg-secondary border-border rounded-sm h-6 text-[10px] text-foreground w-32" />
    </div>
  );
}


// ─── Highlights & Risks Component ──────────────────────────────

function HighlightsRisks({ projectId, project, onRefresh, readOnly }) {
  const { user: hrUser } = useAuth();
  const [adding, setAdding] = React.useState(null); // 'highlight' | 'risk'
  const [title, setTitle] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [editingId, setEditingId] = React.useState(null);
  const [editTitle, setEditTitle] = React.useState('');
  const [editDesc, setEditDesc] = React.useState('');

  const items = project?.highlights_risks || [];
  const highlights = items.filter(i => i.type === 'highlight');
  const risks = items.filter(i => i.type === 'risk');

  const handleAdd = async () => {
    if (!title.trim()) { toast.error('Enter a title'); return; }
    setSaving(true);
    try {
      const newItem = { id: Date.now().toString(), type: adding, title: title.trim(), description: description.trim(), author: hrUser?.name || '' };
      const updated = [...items, newItem];
      await api.updateCompanyInfo(projectId, { highlights_risks: updated });
      setAdding(null); setTitle(''); setDescription('');
      onRefresh();
    } catch { toast.error('Failed to add'); }
    setSaving(false);
  };

  const handleRemove = async (itemId) => {
    try {
      const updated = items.filter(i => i.id !== itemId);
      await api.updateCompanyInfo(projectId, { highlights_risks: updated });
      onRefresh();
    } catch { toast.error('Failed to remove'); }
  };

  const handleStartEdit = (item) => {
    setEditingId(item.id);
    setEditTitle(item.title);
    setEditDesc(item.description || '');
  };

  const handleSaveEdit = async () => {
    if (!editTitle.trim()) { toast.error('Title required'); return; }
    setSaving(true);
    try {
      const updated = items.map(i => i.id === editingId ? { ...i, title: editTitle.trim(), description: editDesc.trim() } : i);
      await api.updateCompanyInfo(projectId, { highlights_risks: updated });
      setEditingId(null);
      onRefresh();
    } catch { toast.error('Failed to save'); }
    setSaving(false);
  };

  return (
    <div className="bg-card border border-border rounded-sm p-4 mt-4" data-testid="highlights-risks-section">
      <div className="flex items-center justify-between mb-3">
        <p className="font-heading text-base text-foreground">Highlights & Risks</p>
        {!readOnly && (
        <div className="flex items-center gap-2">
          <Button onClick={() => { setAdding('highlight'); setTitle(''); setDescription(''); }} size="sm"
            className="bg-emerald-900/30 hover:bg-emerald-900/50 text-emerald-400 border border-emerald-900/30 rounded-sm text-xs h-7 px-2.5"
            data-testid="add-highlight-btn">
            + Highlight
          </Button>
          <Button onClick={() => { setAdding('risk'); setTitle(''); setDescription(''); }} size="sm"
            className="bg-red-900/30 hover:bg-red-900/50 text-red-400 border border-red-900/30 rounded-sm text-xs h-7 px-2.5"
            data-testid="add-risk-btn">
            + Risk
          </Button>
        </div>
        )}
      </div>

      {/* Add form */}
      {adding && (
        <div className={`border rounded-sm p-3 mb-3 ${adding === 'highlight' ? 'border-emerald-800/30 bg-emerald-950/10' : 'border-red-800/30 bg-red-950/10'}`}>
          <p className={`text-[10px] uppercase tracking-wider mb-2 ${adding === 'highlight' ? 'text-emerald-400' : 'text-red-400'}`}>
            New {adding === 'highlight' ? 'Highlight' : 'Risk'}
          </p>
          <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="Title"
            className="bg-secondary border-border rounded-sm h-8 text-sm text-foreground mb-2" data-testid="hr-title-input" autoFocus />
          <Input value={description} onChange={e => setDescription(e.target.value)} placeholder="Description (optional)"
            className="bg-secondary border-border rounded-sm h-8 text-sm text-foreground mb-2" data-testid="hr-description-input" />
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setAdding(null)} className="text-xs h-7">Cancel</Button>
            <Button onClick={handleAdd} disabled={saving || !title.trim()} data-testid="hr-save-btn"
              className={`text-white rounded-sm text-xs h-7 px-3 ${adding === 'highlight' ? 'bg-emerald-600 hover:bg-emerald-500' : 'bg-red-600 hover:bg-red-500'}`}>
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Add'}
            </Button>
          </div>
        </div>
      )}

      {/* Items grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {/* Highlights column */}
        <div>
          {highlights.length === 0 && !adding && (
            <p className="text-[10px] text-muted-foreground/40 text-center py-3">No highlights added</p>
          )}
          {highlights.map(item => (
            <HRItem key={item.id} item={item} color="emerald" editingId={editingId}
              editTitle={editTitle} editDesc={editDesc} setEditTitle={setEditTitle} setEditDesc={setEditDesc}
              onStartEdit={handleStartEdit} onSaveEdit={handleSaveEdit} onCancelEdit={() => setEditingId(null)}
              onRemove={handleRemove} saving={saving} readOnly={readOnly} />
          ))}
        </div>

        {/* Risks column */}
        <div>
          {risks.length === 0 && !adding && (
            <p className="text-[10px] text-muted-foreground/40 text-center py-3">No risks added</p>
          )}
          {risks.map(item => (
            <HRItem key={item.id} item={item} color="red" editingId={editingId}
              editTitle={editTitle} editDesc={editDesc} setEditTitle={setEditTitle} setEditDesc={setEditDesc}
              onStartEdit={handleStartEdit} onSaveEdit={handleSaveEdit} onCancelEdit={() => setEditingId(null)}
              onRemove={handleRemove} saving={saving} readOnly={readOnly} />
          ))}
        </div>
      </div>
    </div>
  );
}


function HRItem({ item, color, editingId, editTitle, editDesc, setEditTitle, setEditDesc, onStartEdit, onSaveEdit, onCancelEdit, onRemove, saving, readOnly }) {
  const isEditing = editingId === item.id;
  const dotColor = color === 'emerald' ? 'bg-emerald-400' : 'bg-red-400';
  const textColor = color === 'emerald' ? 'text-emerald-400' : 'text-red-400';

  if (isEditing && !readOnly) {
    return (
      <div className={`mb-2 border rounded-sm p-2 ${color === 'emerald' ? 'border-emerald-800/30 bg-emerald-950/10' : 'border-red-800/30 bg-red-950/10'}`}
        data-testid={`${color === 'emerald' ? 'highlight' : 'risk'}-edit-${item.id}`}>
        <Input value={editTitle} onChange={e => setEditTitle(e.target.value)} placeholder="Title"
          className="bg-secondary border-border rounded-sm h-7 text-xs text-foreground mb-1.5" autoFocus
          onKeyDown={e => { if (e.key === 'Enter') onSaveEdit(); if (e.key === 'Escape') onCancelEdit(); }} />
        <Input value={editDesc} onChange={e => setEditDesc(e.target.value)} placeholder="Description"
          className="bg-secondary border-border rounded-sm h-7 text-xs text-foreground mb-1.5"
          onKeyDown={e => { if (e.key === 'Enter') onSaveEdit(); if (e.key === 'Escape') onCancelEdit(); }} />
        <div className="flex justify-end gap-1">
          <button onClick={onCancelEdit} className="text-muted-foreground hover:text-foreground p-0.5"><X className="w-3 h-3" /></button>
          <button onClick={onSaveEdit} disabled={saving} className={`${textColor} hover:opacity-80 p-0.5`}><Check className="w-3 h-3" /></button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-start gap-2 mb-2 group" data-testid={`${color === 'emerald' ? 'highlight' : 'risk'}-${item.id}`}>
      <div className={`w-1.5 h-1.5 rounded-full ${dotColor} mt-1.5 shrink-0`} />
      <div className={`flex-1 min-w-0 ${!readOnly ? 'cursor-pointer' : ''}`} onClick={() => !readOnly && onStartEdit(item)}>
        <p className={`text-sm font-medium ${textColor}`}>
          {item.title}
          {item.author && (
            <span className="text-[10px] text-muted-foreground/60 font-normal ml-1.5" data-testid="hr-author">
              — {item.author.split(' ')[0]}
            </span>
          )}
        </p>
        {item.description && <p className="text-[11px] text-muted-foreground mt-0.5">{item.description}</p>}
      </div>
      {!readOnly && (
        <>
          <button onClick={() => onStartEdit(item)}
            className="opacity-0 group-hover:opacity-100 text-muted-foreground/30 hover:text-foreground transition-all p-0.5 shrink-0">
            <Pencil className="w-3 h-3" />
          </button>
          <button onClick={() => onRemove(item.id)}
            className="opacity-0 group-hover:opacity-100 text-muted-foreground/30 hover:text-red-400 transition-all p-0.5 shrink-0">
            <X className="w-3 h-3" />
          </button>
        </>
      )}
    </div>
  );
}


// ─── Shareholder Pie Chart ─────────────────────────────────────

const PIE_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];

function ShareholderPieChart({ projectId, project, onRefresh, isAcMember }) {
  const [adding, setAdding] = React.useState(false);
  const [name, setName] = React.useState('');
  const [age, setAge] = React.useState('');
  const [pct, setPct] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [hoveredIdx, setHoveredIdx] = React.useState(null);
  const [jointMode, setJointMode] = React.useState(false);
  const [jointSel, setJointSel] = React.useState([]);

  const shareholders = Array.isArray(project?.shareholders) ? project.shareholders : [];
  const textFallback = (project?.executive_summary || {}).shareholder_structure || '';
  const jointRec = project?.joint_recipients;

  const toggleJointSel = (n) => setJointSel(s =>
    s.includes(n) ? s.filter(x => x !== n) : (s.length < 2 ? [...s, n] : s));

  const saveJoint = async () => {
    try {
      const r = await api.setJointRecipients(projectId, jointSel);
      toast.success(`Gemeinsames Anschreiben: ${r.data.joint_recipients?.label || ''}`);
      setJointMode(false); setJointSel([]);
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen'); }
  };

  const clearJoint = async () => {
    try { await api.setJointRecipients(projectId, []); onRefresh(); }
    catch { toast.error('Entfernen fehlgeschlagen'); }
  };

  const handleAdd = async () => {
    if (!name.trim() || !pct) return;
    setSaving(true);
    try {
      const updated = [...shareholders, { id: Date.now().toString(), name: name.trim(), age: age.trim(), pct: parseFloat(pct) }];
      await api.updateCompanyInfo(projectId, { shareholders: updated });
      setAdding(false); setName(''); setAge(''); setPct('');
      onRefresh();
    } catch { toast.error('Failed to add'); }
    setSaving(false);
  };

  const handleRemove = async (id) => {
    try {
      await api.updateCompanyInfo(projectId, { shareholders: shareholders.filter(s => s.id !== id) });
      onRefresh();
    } catch { toast.error('Failed to remove'); }
  };

  const total = shareholders.reduce((sum, s) => sum + (s.pct || 0), 0);

  // Build SVG pie slices
  const slices = [];
  let cumAngle = -90; // start at top
  shareholders.forEach((s, i) => {
    const angle = (s.pct / 100) * 360;
    const startRad = (cumAngle * Math.PI) / 180;
    const endRad = ((cumAngle + angle) * Math.PI) / 180;
    const x1 = 50 + 40 * Math.cos(startRad);
    const y1 = 50 + 40 * Math.sin(startRad);
    const x2 = 50 + 40 * Math.cos(endRad);
    const y2 = 50 + 40 * Math.sin(endRad);
    const largeArc = angle > 180 ? 1 : 0;
    const r = hoveredIdx === i ? 42 : 40;
    const hx1 = 50 + r * Math.cos(startRad);
    const hy1 = 50 + r * Math.sin(startRad);
    const hx2 = 50 + r * Math.cos(endRad);
    const hy2 = 50 + r * Math.sin(endRad);
    const path = s.pct >= 100
      ? `M 50 10 A 40 40 0 1 1 49.99 10 Z`
      : `M 50 50 L ${hx1} ${hy1} A ${r} ${r} 0 ${largeArc} 1 ${hx2} ${hy2} Z`;
    slices.push({ path, color: PIE_COLORS[i % PIE_COLORS.length], s, i });
    cumAngle += angle;
  });

  return (
    <div data-testid="shareholder-chart">
      <p className="text-[10px] text-foreground/40 uppercase tracking-wider mb-2">Shareholder Structure</p>
      <div className="flex items-start gap-4">
        {/* Pie */}
        {shareholders.length > 0 && (
          <div className="shrink-0" style={{ width: '120px', height: '120px' }}>
            <svg viewBox="0 0 100 100" className="w-full h-full">
              {shareholders.length === 0 && <circle cx="50" cy="50" r="40" fill="#1e293b" stroke="#475569" strokeWidth="1" />}
              {slices.map(({ path, color, s, i }) => (
                <path key={i} d={path} fill={color} stroke="#0f172a" strokeWidth="0.5"
                  className="transition-all duration-150 cursor-pointer"
                  style={{ opacity: hoveredIdx !== null && hoveredIdx !== i ? 0.4 : 1 }}
                  onMouseEnter={() => setHoveredIdx(i)}
                  onMouseLeave={() => setHoveredIdx(null)} />
              ))}
              {hoveredIdx !== null && shareholders[hoveredIdx] && (
                <text x="50" y="50" textAnchor="middle" dominantBaseline="central"
                  className="text-[7px] fill-white font-medium pointer-events-none">
                  {shareholders[hoveredIdx].pct}%
                </text>
              )}
            </svg>
          </div>
        )}

        {/* List */}
        <div className="flex-1 min-w-0">
          {shareholders.map((s, i) => (
            <div key={s.id} className="flex items-center gap-2 mb-1.5 group"
              onMouseEnter={() => setHoveredIdx(i)} onMouseLeave={() => setHoveredIdx(null)}>
              <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
              {jointMode && (
                <input type="checkbox" checked={jointSel.includes(s.name)} onChange={() => toggleJointSel(s.name)}
                  className="accent-pink-500 w-3.5 h-3.5 shrink-0 cursor-pointer" data-testid={`joint-select-${i}`} />
              )}
              {!isAcMember ? (
                <ContactHoverCard name={s.name} company={project?.name || ''} projectId={projectId} canCreate>
                  <span className="text-sm text-foreground truncate cursor-pointer underline decoration-dotted decoration-foreground/20 underline-offset-2 hover:decoration-foreground/60"
                    data-testid={`shareholder-name-${i}`}>{s.name}</span>
                </ContactHoverCard>
              ) : (
                <span className="text-sm text-foreground truncate">{s.name}</span>
              )}
              {s.age && <span className="text-[10px] text-muted-foreground">({s.age})</span>}
              <span className="text-xs font-mono text-foreground/60 ml-auto shrink-0">{s.pct}%</span>
              {!isAcMember && (
                <button onClick={() => handleRemove(s.id)}
                  className="opacity-0 group-hover:opacity-100 text-muted-foreground/30 hover:text-red-400 transition-all p-0.5 shrink-0">
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
          ))}
          {total > 0 && total !== 100 && (
            <p className="text-[9px] text-amber-400 mt-1">Total: {total.toFixed(1)}% {total < 100 ? `(${(100 - total).toFixed(1)}% unallocated)` : '(exceeds 100%)'}</p>
          )}

          {!isAcMember && !adding && (
            <button onClick={() => setAdding(true)} className="text-[10px] text-blue-400 hover:text-blue-300 mt-1" data-testid="add-shareholder-btn">
              + Add Shareholder
            </button>
          )}
          {!isAcMember && jointRec && (
            <p className="flex items-center gap-1.5 text-[10px] text-pink-300 mt-1.5" data-testid="joint-recipients-badge">
              💑 Gemeinsames Anschreiben: {jointRec.label}
              <button onClick={clearJoint} title="Gemeinsames Anschreiben entfernen" data-testid="joint-recipients-clear"
                className="text-muted-foreground/40 hover:text-red-400 transition-colors"><X className="w-3 h-3" /></button>
            </p>
          )}
          {!isAcMember && !jointRec && shareholders.length >= 2 && !jointMode && (
            <button onClick={() => setJointMode(true)} data-testid="joint-recipients-start"
              className="block text-[10px] text-pink-400/80 hover:text-pink-300 mt-1">
              💑 Gemeinsam anschreiben (z.B. Eheleute)
            </button>
          )}
          {jointMode && (
            <div className="flex items-center gap-2 mt-1.5">
              <span className="text-[10px] text-muted-foreground">{jointSel.length}/2 ausgewählt</span>
              <button onClick={saveJoint} disabled={jointSel.length !== 2} data-testid="joint-recipients-save"
                className="text-[10px] px-2 py-0.5 rounded-sm border border-pink-500/40 text-pink-300 hover:bg-pink-500/10 disabled:opacity-40 transition-colors">
                Übernehmen
              </button>
              <button onClick={() => { setJointMode(false); setJointSel([]); }}
                className="text-[10px] text-muted-foreground hover:text-foreground">Abbrechen</button>
            </div>
          )}
          {adding && (
            <div className="mt-2 space-y-1.5 border border-border rounded-sm p-2 bg-secondary/20">
              <div className="flex gap-1.5">
                <Input value={name} onChange={e => setName(e.target.value)} placeholder="Name"
                  className="bg-secondary border-border rounded-sm h-7 text-xs text-foreground flex-1" autoFocus data-testid="shareholder-name" />
                <Input value={age} onChange={e => setAge(e.target.value)} placeholder="Age"
                  className="bg-secondary border-border rounded-sm h-7 text-xs text-foreground w-16" data-testid="shareholder-age" />
                <Input type="number" value={pct} onChange={e => setPct(e.target.value)} placeholder="%"
                  className="bg-secondary border-border rounded-sm h-7 text-xs text-foreground w-16 font-mono" data-testid="shareholder-pct"
                  onKeyDown={e => { if (e.key === 'Enter') handleAdd(); if (e.key === 'Escape') setAdding(false); }} />
              </div>
              <div className="flex justify-end gap-1">
                <button onClick={() => setAdding(false)} className="text-muted-foreground hover:text-foreground p-0.5"><X className="w-3 h-3" /></button>
                <button onClick={handleAdd} disabled={saving || !name.trim() || !pct} className="text-blue-400 hover:text-blue-300 p-0.5"><Check className="w-3 h-3" /></button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


function ManagementTeamField({ label, field, placeholder, editing, value, onChange }) {
  const currentYear = new Date().getFullYear();
  // Split into individual members and calculate age from birth years
  const members = React.useMemo(() => {
    if (!value) return [];
    return value.split(/[;\n]/).map(m => m.trim()).filter(Boolean).map(m => {
      // Look for 4-digit birth year in parentheses: (1956), (1982)
      const birthMatch = m.match(/\((\d{4})\)/);
      let age = null;
      let display = m;
      if (birthMatch) {
        const birthYear = parseInt(birthMatch[1]);
        if (birthYear >= 1920 && birthYear <= 2010) {
          age = currentYear - birthYear;
          // Replace birth year with age: (1956) → (68 yrs)
          display = m.replace(birthMatch[0], `(${age} yrs)`);
        }
      }
      return { display, age, initial: m.replace(/^[^a-zA-ZÀ-ÿ]*/, '').charAt(0).toUpperCase() || '?' };
    });
  }, [value, currentYear]);

  return (
    <div>
      <p className="text-[10px] text-foreground/40 uppercase tracking-wider mb-1">{label}</p>
      {editing ? (
        <textarea value={value || ''} onChange={e => onChange(field, e.target.value)} placeholder={placeholder}
          rows={3} className="w-full bg-secondary border border-border rounded-sm text-sm text-foreground p-2.5 outline-none resize-none leading-relaxed" />
      ) : members.length > 0 ? (
        <div className="space-y-1.5">
          {members.map((m, i) => (
            <div key={i} className="flex items-start gap-2">
              <div className="w-5 h-5 rounded-full bg-blue-600/15 flex items-center justify-center text-blue-400 text-[9px] font-bold font-mono shrink-0 mt-0.5">
                {m.initial}
              </div>
              <p className="text-sm text-foreground/80 leading-snug">{m.display}</p>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-foreground/20">{'\u2014'}</p>
      )}
    </div>
  );
}
